import React, { useState, useEffect, useCallback } from "react";
import {
  MessageCircle,
  X,
  ZoomIn,
  ChevronLeft,
  ChevronRight,
  Wrench,
  ShieldCheck,
  Armchair,
  UserCheck,
} from "lucide-react";
import { company, filo, filoCategories } from "../mock";
import { Reveal, PageHero } from "../components/Shared";
import useSEO from "../hooks/useSEO";

const fleetFeatures = [
  {
    icon: Wrench,
    title: "Düzenli Bakım",
    text: "Tüm araçlarımızın periyodik bakımları düzenli olarak yapılır.",
  },
  {
    icon: ShieldCheck,
    title: "Güvenlik Donanımı",
    text: "Yasal standartlara tam uyumlu, güvenlik donanımlı araçlar.",
  },
  {
    icon: Armchair,
    title: "Konforlu İç Mekan",
    text: "Klimalı, bakımlı ve konforlu iç mekanlarla keyifli yolculuk.",
  },
  {
    icon: UserCheck,
    title: "Profesyonel Sürücü",
    text: "Mesleki yeterlilik belgeli, deneyimli sürücü kadrosu.",
  },
];

const AracFilomuz = () => {
  useSEO({
    title: "Araç Filomuz",
    description:
      "Nikomedya Turizm araç filosu: otobüs, midibüs, minibüs, VIP araçlar ve binek araçlar. Düzenli bakımlı, konforlu ve güvenli araçlarla profesyonel taşımacılık.",
  });

  const [category, setCategory] = useState("Tümü");
  const [lightboxIndex, setLightboxIndex] = useState(null);

  const filtered =
    category === "Tümü" ? filo : filo.filter((f) => f.category === category);

  const closeLightbox = useCallback(() => setLightboxIndex(null), []);
  const nextImage = useCallback(
    () => setLightboxIndex((i) => (i === null ? null : (i + 1) % filtered.length)),
    [filtered.length]
  );
  const prevImage = useCallback(
    () =>
      setLightboxIndex((i) =>
        i === null ? null : (i - 1 + filtered.length) % filtered.length
      ),
    [filtered.length]
  );

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") closeLightbox();
      if (e.key === "ArrowRight") nextImage();
      if (e.key === "ArrowLeft") prevImage();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [closeLightbox, nextImage, prevImage]);

  const selected = lightboxIndex !== null ? filtered[lightboxIndex] : null;

  return (
    <div>
      <PageHero
        title="Araç Filomuz"
        subtitle="Otobüsten VIP araçlara uzanan geniş filomuzla her ihtiyaca uygun, güvenli ve konforlu ulaşım çözümleri sunuyoruz."
      />

      {/* Filter + Gallery */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <div className="flex flex-wrap items-center gap-3 mb-12">
              {filoCategories.map((c) => (
                <button
                  key={c}
                  onClick={() => {
                    setCategory(c);
                    setLightboxIndex(null);
                  }}
                  className={`px-5 py-2.5 rounded-full text-sm font-bold transition-all duration-300 ${
                    category === c
                      ? "bg-[#0d2b4f] text-white shadow-lg"
                      : "bg-slate-100 text-[#5b6b7d] hover:bg-[#e8f5fc] hover:text-[#1e9cd7]"
                  }`}
                >
                  {c}
                </button>
              ))}
            </div>
          </Reveal>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map((f, i) => (
              <Reveal key={f.id} delay={(i % 3) * 80}>
                <button
                  onClick={() => setLightboxIndex(i)}
                  className="news-card group block w-full text-left bg-white rounded-2xl overflow-hidden border border-slate-200"
                >
                  <div className="relative h-64 overflow-hidden bg-slate-50">
                    <img
                      src={f.image}
                      alt={f.name}
                      loading="lazy"
                      className="w-full h-full object-cover"
                    />
                    <span className="absolute top-4 left-4 bg-[#050e1d]/75 backdrop-blur-sm text-white text-[11px] font-bold px-3.5 py-1.5 rounded-full">
                      {f.category}
                    </span>
                    <div className="absolute inset-0 bg-[#050e1d]/0 group-hover:bg-[#050e1d]/35 transition-colors duration-300 flex items-center justify-center">
                      <span className="w-12 h-12 rounded-full bg-white text-[#0d2b4f] flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                        <ZoomIn size={20} />
                      </span>
                    </div>
                  </div>
                  <div className="p-6">
                    <h3 className="text-[16px] font-semibold text-[#0d2b4f] tracking-tight group-hover:text-[#1e9cd7] transition-colors">
                      {f.name}
                    </h3>
                    <div className="mt-3 flex flex-wrap gap-2">
                      {f.tags.map((t) => (
                        <span
                          key={t}
                          className="bg-[#e8f5fc] text-[#157cb0] text-[11px] font-bold px-3 py-1 rounded-full"
                        >
                          {t}
                        </span>
                      ))}
                    </div>
                  </div>
                </button>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Fleet standards */}
      <section className="py-24 bg-[#f5f8fb]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <div className="text-center max-w-2xl mx-auto mb-14">
              <p className="section-label text-[#1e9cd7] text-[11px] font-bold uppercase mb-4">
                Filo Standartlarımız
              </p>
              <h2 className="text-2xl sm:text-3xl font-bold text-[#0d2b4f] tracking-tight">
                Her Araçta Aynı Kalite
              </h2>
            </div>
          </Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {fleetFeatures.map((f, i) => (
              <Reveal key={f.title} delay={i * 80}>
                <div className="feature-card h-full bg-white border border-slate-100 rounded-2xl p-7">
                  <div className="feature-icon w-14 h-14 rounded-xl bg-[#e8f5fc] text-[#1e9cd7] flex items-center justify-center">
                    <f.icon size={25} />
                  </div>
                  <h3 className="mt-5 text-lg font-extrabold text-[#0d2b4f] tracking-tight">
                    {f.title}
                  </h3>
                  <p className="mt-2.5 text-sm leading-relaxed text-[#5b6b7d]">
                    {f.text}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal>
            <div className="mt-16 bg-[#0d2b4f] rounded-3xl px-8 sm:px-14 py-12 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
              <div>
                <h3 className="text-xl sm:text-2xl font-bold text-white tracking-tight">
                  İhtiyacınıza uygun aracı birlikte seçelim
                </h3>
                <p className="mt-2 text-white/60">
                  Yolcu sayısı ve güzergahınızı iletin, size en uygun çözümü önerelim.
                </p>
              </div>
              <a
                href={company.whatsappHref}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-whatsapp inline-flex items-center gap-2 px-7 py-3.5 rounded-full font-bold text-sm shrink-0"
              >
                <MessageCircle size={18} />
                WhatsApp'tan Teklif Al
              </a>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Lightbox */}
      {selected && (
        <div
          className="fixed inset-0 z-[100] bg-[#050e1d]/92 backdrop-blur-sm flex items-center justify-center p-4 sm:p-12"
          onClick={closeLightbox}
        >
          <button
            className="absolute top-6 right-6 w-12 h-12 rounded-full bg-white/10 border border-white/20 text-white flex items-center justify-center hover:bg-white hover:text-[#0d2b4f] transition-colors z-10"
            aria-label="Kapat"
          >
            <X size={22} />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              prevImage();
            }}
            className="absolute left-3 sm:left-8 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 border border-white/20 text-white flex items-center justify-center hover:bg-white hover:text-[#0d2b4f] transition-colors z-10"
            aria-label="Önceki"
          >
            <ChevronLeft size={22} />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              nextImage();
            }}
            className="absolute right-3 sm:right-8 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 border border-white/20 text-white flex items-center justify-center hover:bg-white hover:text-[#0d2b4f] transition-colors z-10"
            aria-label="Sonraki"
          >
            <ChevronRight size={22} />
          </button>
          <div
            className="max-w-5xl w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={selected.image}
              alt={selected.name}
              className="w-full max-h-[75vh] object-contain rounded-2xl"
            />
            <div className="mt-5 flex flex-wrap items-center justify-between gap-3">
              <div>
                <h3 className="text-white font-extrabold text-xl tracking-tight">
                  {selected.name}
                </h3>
                <p className="text-white/50 text-sm mt-0.5">
                  {selected.category} — {selected.tags.join(" · ")}
                </p>
              </div>
              <span className="text-white/40 text-sm font-bold">
                {lightboxIndex + 1} / {filtered.length}
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AracFilomuz;
